(() => {
  const forms = document.querySelectorAll('[data-croma-product-form]');

  if (!forms.length) {
    return;
  }

  class CromaProductForm {
    constructor(form) {
      this.form = form;
      this.root = form.closest('.croma-main-product') || form;
      this.variantSelect = this.root.querySelector('[data-product-variant-select]');
      this.variantInput = form.querySelector('[data-product-variant-input]');
      this.variantsScript = form.querySelector('[data-product-variants]');
      this.priceTarget = this.root.querySelector('[data-product-price]');
      this.comparePriceTarget = this.root.querySelector('[data-product-compare-price]');
      this.skuTarget = this.root.querySelector('[data-product-sku]');
      this.skuValueTarget = this.root.querySelector('[data-product-sku-value]');
      this.submitButton = this.root.querySelector('[data-product-submit]');
      this.message = this.root.querySelector('[data-product-form-message]');
      this.mediaItems = this.root.querySelectorAll('[data-product-media-id]');
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.variants = this.parseVariants();

      this.variantSelect?.addEventListener('change', this.onVariantChange);
      form.addEventListener('submit', this.onSubmit);
    }

    parseVariants() {
      if (!this.variantsScript) {
        return [];
      }

      try {
        return JSON.parse(this.variantsScript.textContent);
      } catch (error) {
        return [];
      }
    }

    onVariantChange = () => {
      const variant = this.currentVariant;

      if (!variant) {
        return;
      }

      this.clearMessage();
      this.updatePrice(variant, this.selectedOption);
      this.updateBadges(this.selectedOption);
      this.updateVariantInput(variant);
      this.updateSku(variant);
      this.updateSubmit(variant);
      this.updateMedia(this.selectedOption);
      this.updateUrl(variant, this.selectedOption);

      this.root.dispatchEvent(
        new CustomEvent('croma:variant:change', {
          bubbles: true,
          detail: { variant },
        })
      );
    };

    onSubmit = async (event) => {
      if (event.defaultPrevented || event.submitter?.name !== 'add') {
        return;
      }

      event.preventDefault();
      this.setLoading(true);
      this.clearMessage();

      try {
        const response = await fetch(`${this.routesRoot}cart/add.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json'
          },
          body: new FormData(this.form)
        });

        if (!response.ok) {
          throw new Error(this.form.dataset.errorLabel || '');
        }

        const drawer = document.querySelector('croma-cart-drawer');

        if (drawer?.open) {
          await drawer.open(event.submitter || this.submitButton);
        } else {
          window.location.href = `${this.routesRoot}cart`;
        }
      } catch (error) {
        this.showMessage(error.message || this.form.dataset.errorLabel || '');
      } finally {
        this.setLoading(false);
      }
    };

    updatePrice(variant, option) {
      if (this.priceTarget && option?.dataset.variantPrice) {
        this.priceTarget.textContent = option.dataset.variantPrice;
      }

      if (!this.comparePriceTarget) {
        return;
      }

      if (
        variant.compare_at_price &&
        variant.compare_at_price > variant.price &&
        option?.dataset.variantComparePrice
      ) {
        this.comparePriceTarget.textContent = option.dataset.variantComparePrice;
        this.comparePriceTarget.hidden = false;
      } else {
        this.comparePriceTarget.textContent = '';
        this.comparePriceTarget.hidden = true;
      }
    }

    updateBadges(option) {
      if (option?.dataset.variantBadges === undefined) return;
      // Markup is rendered by Liquid; custom badge text is escaped there.
      this.root.querySelectorAll('[data-product-badges]').forEach((target) => {
        target.innerHTML = option.dataset.variantBadges;
      });
    }

    updateVariantInput(variant) {
      if (!this.variantInput) {
        return;
      }

      this.variantInput.value = variant.id;
    }

    updateSku(variant) {
      if (!this.skuTarget || !this.skuValueTarget) {
        return;
      }

      if (variant.sku) {
        this.skuValueTarget.textContent = variant.sku;
        this.skuTarget.hidden = false;
      } else {
        this.skuValueTarget.textContent = '';
        this.skuTarget.hidden = true;
      }
    }

    updateSubmit(variant) {
      if (!this.submitButton) {
        return;
      }

      this.submitButton.disabled = !variant.available;
      this.submitButton.textContent = variant.available
        ? this.form.dataset.addLabel
        : this.form.dataset.soldOutLabel;
    }

    updateMedia(option) {
      const mediaId = option?.dataset.variantMediaId;

      if (!mediaId) {
        return;
      }

      const activeMedia = Array.from(this.mediaItems).find((media) => media.dataset.productMediaId === mediaId);

      if (!activeMedia || activeMedia.classList.contains('is-active')) {
        return;
      }

      this.mediaItems.forEach((media) => {
        media.classList.toggle('is-active', media === activeMedia);
      });

      activeMedia.scrollIntoView({
        behavior: this.reducedMotionQuery.matches ? 'auto' : 'smooth',
        block: 'nearest',
        inline: 'start',
      });
    }

    updateUrl(variant, option) {
      if (!window.history.replaceState || !option?.dataset.variantUrl) {
        return;
      }

      window.history.replaceState({ variantId: variant.id }, '', option.dataset.variantUrl);
    }

    setLoading(isLoading) {
      if (!this.submitButton) {
        return;
      }

      this.submitButton.disabled = isLoading || (this.currentVariant && !this.currentVariant.available);
      this.submitButton.textContent = isLoading
        ? this.form.dataset.addingLabel
        : this.currentVariant && !this.currentVariant.available
          ? this.form.dataset.soldOutLabel
          : this.form.dataset.addLabel;
    }

    clearMessage() {
      if (!this.message) {
        return;
      }

      this.message.textContent = '';
      this.message.hidden = true;
    }

    showMessage(message) {
      if (!this.message) {
        return;
      }

      this.message.textContent = message;
      this.message.hidden = false;
    }

    get currentVariant() {
      const selectedId = Number(this.variantSelect?.value || this.variantInput?.value || this.form.querySelector('[name="id"]')?.value);
      return this.variants.find((variant) => Number(variant.id) === selectedId);
    }

    get selectedOption() {
      return this.variantSelect?.selectedOptions?.[0] || null;
    }

    get routesRoot() {
      return window.Shopify?.routes?.root || '/';
    }
  }

  forms.forEach((form) => {
    new CromaProductForm(form);
  });
})();
